import styled from '@emotion/styled';
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FC, ReactNode } from 'react';

type Size = 'small' | 'medium';

type Placement =
  | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    lineHeight: 1.55,
    fontWeight: 400,
    whiteSpace: 'pre-line',
    padding: '0.75rem 0.9rem',
    maxWidth: 340,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.14)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    '&::before': {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      boxSizing: 'border-box',
    },
  },
}));

const InfoButton = styled.button<{ size: Size }>(({ theme, size }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: theme.palette.text.disabled,
  cursor: 'help',
  lineHeight: 0,
  borderRadius: '50%',
  transition: 'color 150ms ease',
  '& svg': {
    fontSize: size === 'small' ? '1rem' : '1.2rem',
  },
  '&:hover': {
    color: theme.palette.primary.main,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
    color: theme.palette.primary.main,
  },
}));

interface Props {
  /** Helper text shown in the tooltip. Newlines (\n) render as line breaks. */
  title: ReactNode;
  placement?: Placement;
  size?: Size;
  className?: string;
  /** Accessible label; falls back to `title` when it is a plain string. */
  ariaLabel?: string;
}

/**
 * Unified info indicator: a small "i" icon that reveals helper text on hover/focus
 * in a light, readable tooltip. Use anywhere a field needs an explanation.
 */
export const QcsInfoTooltip: FC<Props> = ({ title, placement = 'top', size = 'small', className, ariaLabel }) => {
  return (
    <LightTooltip title={title} placement={placement}>
      <InfoButton type="button" size={size} className={className} aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}>
        <InfoOutlinedIcon />
      </InfoButton>
    </LightTooltip>
  );
};
